import React from 'react';
import {useQuery} from "@apollo/client";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {Box, Button, Card, CardContent} from "@mui/material";
import {CircularProgress, Typography} from "@mui/joy";
import {Error} from "@dreebit-registry/vsm-microservice-components";
import {AssetRemoveEvents} from "../../graphql/queries/assetRemoveEvents";
import {sanitizeErrorForDisplay} from "../../utils/resolveErrorMessage";
import formatEventEnum from "../../utils/formatEventEnum";
import {useDeclarationForEvent} from "../../hooks/useDeclarationForEvent";
import {useOpenContaminationDeclaration} from "../../hooks/useOpenContaminationDeclaration";
import ContaminationDeclarationStatusChip from "../ContaminationDeclarationStatus/StatusChip";
import PrintDeclarationButton from "../ContaminationDeclarationStatus/PrintDeclarationButton";

interface RemoveEvent {
    id: string;
    insert?: string | null;
    eventType?: string | null;
    user?: { name?: string | null } | null;
    installedBaseNode?: { pathNodes?: Array<{ title?: string | null } | null> | null } | null;
}

const DeclarationEventCard = ({event}: {event: RemoveEvent}) => {
    const {t} = useTranslation();
    const {declaration, readiness, available, loading, error} = useDeclarationForEvent(event.id);
    const {openForEvent, busy, canCreate} = useOpenContaminationDeclaration();
    const path = (event.installedBaseNode?.pathNodes ?? []).map((n) => n?.title).filter(Boolean).join(' / ');
    // Starting is gated on the owning client's region; opening an existing one is not.
    const canStart = canCreate && available;

    return (
        <Card sx={{border: "1px solid #cccccc", borderRadius: "4px", boxShadow: "none"}} data-testid="asset-doc-card">
            <CardContent sx={{padding: "16px 20px", "&:last-child": {paddingBottom: "16px"}, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                <Box sx={{flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: '2px'}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                        <Typography level="titleSmall">{formatEventEnum(event.eventType, t)}</Typography>
                        <ContaminationDeclarationStatusChip eventId={event.id} eventType={event.eventType}/>
                    </Box>
                    <Typography level="bodySmall" sx={{color: 'text.secondary'}}>
                        {[event.insert ? moment(event.insert).format('L LT') : null, event.user?.name].filter(Boolean).join(' · ')}
                    </Typography>
                    {path && (
                        <Typography level="bodySmall" sx={{color: 'text.secondary'}}>{path}</Typography>
                    )}
                </Box>
                {!loading && !error && (declaration || canStart) && (
                    <Box sx={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                        <Button
                            variant={declaration ? 'outlined' : 'contained'}
                            size="small"
                            onClick={() => openForEvent(event.id)}
                            disabled={busy}
                            data-testid="asset-doc-open"
                        >
                            {declaration ? t('Open declaration') : t('Create declaration')}
                        </Button>
                        {/* Own hook instance per card, so a print spinner stays on its own row. */}
                        {declaration && readiness?.ready && (
                            <PrintDeclarationButton
                                declarationId={declaration.id}
                                data-testid="asset-doc-print"
                            />
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

/**
 * Asset detail section "Contamination Declarations": one entry per removal of
 * this asset (a declaration belongs to exactly one ASSET_REMOVE event). The
 * backend has no declaration list query, so the list is derived from the
 * asset's REMOVE events with one lookup each — removals per asset are few.
 */
const AssetContaminationDeclarations = ({assetId}: {assetId: string}) => {
    const {t} = useTranslation();
    const {data, loading, error} = useQuery(AssetRemoveEvents, {
        variables: {assetId},
        skip: !assetId,
        fetchPolicy: 'cache-and-network',
    });
    const events: RemoveEvent[] = (data?.installedBaseEventList?.installedBaseEvents ?? []).filter(Boolean);

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, p: '8px 12px', flex: 1, minHeight: 0, overflow: 'auto'}} data-testid="asset-doc-list">
            <Box>
                <Typography level="titleMedium">{t('Contamination Declarations')}</Typography>
                <Typography level="bodySmall" sx={{color: 'text.secondary'}}>
                    {t('A declaration of contamination describes the removed asset of one removal or exchange event. It can be started right after the event or later from here.')}
                </Typography>
            </Box>

            {loading && events.length === 0 && (
                <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress variant="soft"/></Box>
            )}
            {error && events.length === 0 && <Error errorObject={sanitizeErrorForDisplay(error, t)}/>}
            {!loading && !error && events.length === 0 && (
                <Typography level="body-md" sx={{color: 'text.secondary'}} data-testid="asset-doc-empty">
                    {t('No removals recorded for this asset yet.')}
                </Typography>
            )}

            {events.map((event) => <DeclarationEventCard key={event.id} event={event}/>)}
        </Box>
    );
};

export default AssetContaminationDeclarations;
